import { useState } from "react";
import { Table, Tag } from "antd";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from "recharts";
import { Users, TrendingUp } from "lucide-react";

/* =================================================
   EDUCATION DASHBOARD
================================================= */

const EducationDashboard = ({ config }) => {
  const [data, setData] = useState({
    staffEntry: 0,
    staffExit: 0,
    staffInside: 0,
    attendanceTrend: [],
    entryExitLogs: []
  });

  /* =================================================
     APIs
  ================================================= */

  const MetricCard = ({ title, value, subtitle, icon: Icon, color }) => {

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300">

      <div className="flex items-center justify-between mb-3">

        <div className={`p-3 rounded-xl bg-gradient-to-br ${color}`}>
          <Icon className="text-white w-5 h-5"/>
        </div>

        <span className="text-xs text-gray-400">
          {subtitle}
        </span>

      </div>

      <p className="text-sm text-gray-500 mb-1">{title}</p>

      <h3 className="text-3xl font-bold text-gray-900">
        {value}
      </h3>

    </div>
  );
};

 const AttendanceTrendGraph = () => (

  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">

    <div className="flex items-center gap-2 mb-4">

      <div className="p-2 bg-indigo-500 rounded-lg">
        <TrendingUp size={16} className="text-white"/>
      </div>

      <h3 className="font-semibold text-gray-700">
        Attendance Trend
      </h3>

    </div>

    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data.attendanceTrend}>
        <defs>
          <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>

        <XAxis dataKey="date" stroke="#64748b"/>

        <YAxis stroke="#64748b"/>

        <Tooltip/>

        <Area
          type="monotone"
          dataKey="count"
          stroke="#6366f1"
          fillOpacity={1}
          fill="url(#colorAttendance)"
        />

      </AreaChart>
    </ResponsiveContainer>

  </div>
);

 const GateEntryExitTable = () => {

  const columns = [

    { title: "Staff ID", dataIndex: "staffId" },

    { title: "Name", dataIndex: "name" },

    {
      title: "Entry Time",
      dataIndex: "entry"
    },

    {
      title: "Exit Time",
      dataIndex: "exit",
      render: v => v || "-"
    },

    {
      title: "Status",
      dataIndex: "status",
      render: status =>
        status === "Inside"
          ? <Tag color="green">Inside</Tag>
          : <Tag color="blue">Exited</Tag>
    }

  ];

  return (

    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">

      <h3 className="font-semibold text-gray-700 mb-4">
        Gate Entry Exit Logs
      </h3>

      <Table
        columns={columns}
        dataSource={data.entryExitLogs}
        size="middle"
        pagination={{ pageSize: 6 }}
      />

    </div>

  );

};

  /* =================================================
     WIDGET REGISTRY
  ================================================= */

 const WIDGETS = {

  "Staff Entry Count": () => (
    <MetricCard
      title="Staff Entry Count"
      value={data.staffEntry}
      subtitle="Today"
      icon={Users}
      color="from-blue-500 to-indigo-600"
    />
  ),

  "Staff Exit Count": () => (
    <MetricCard
      title="Staff Exit Count"
      value={data.staffExit}
      subtitle="Today"
      icon={Users}
      color="from-emerald-500 to-green-600"
    />
  ),

  "Staff Inside Campus": () => (
    <MetricCard
      title="Staff Inside Campus"
      value={data.staffInside}
      subtitle="Currently"
      icon={Users}
      color="from-purple-500 to-indigo-600"
    />
  ),

  "Late Staff Today": () => (
    <MetricCard
      title="Late Staff Today"
      value={data.lateStaff || 0}
      subtitle="Delayed"
      icon={Users}
      color="from-yellow-500 to-orange-500"
    />
  ),

  "Early Exit Count": () => (
    <MetricCard
      title="Early Exit Count"
      value={data.earlyExit || 0}
      subtitle="Left early"
      icon={Users}
      color="from-pink-500 to-rose-500"
    />
  ),

  "Active Cameras": () => (
    <MetricCard
      title="Active Cameras"
      value={data.activeCameras || 0}
      subtitle="Online"
      icon={Users}
      color="from-cyan-500 to-blue-500"
    />
  ),

  "Gate Entry Exit Log": () => <GateEntryExitTable />,

  "Staff Attendance Trend": () => <AttendanceTrendGraph />

};

  return (

    <div className="max-w-7xl mx-auto">

      {config?.rows?.map((row, rowIdx) => {
          return (
            <div
            key={rowIdx}
            className={`grid grid-cols-${row.cards.length} gap-6 mb-6`}
          >
  
            {row.cards.map((card, idx) => {
  
              const Widget = WIDGETS[card.name];
  
              if (!Widget) return null;
  
              return (
                <div key={idx}>
                  <Widget />
                </div>
              );
  
            })}
  
          </div>
          );
      })}

    </div>

  );

};

export default EducationDashboard;